import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  PlayArrow,
  Edit
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { noticeAPI } from '../services/api';
import { format } from 'date-fns';

interface Notice {
  id: string;
  notice_number: string;
  status: string;
  min_sell_price: number;
  min_price_allowed?: number;
  share_quantity_total: number;
  start_date: string;
  end_date: string;
  issue_date?: string;
  activated_at?: string;
  completed_at?: string;
  expired_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  notes?: string;
  created_at: string;
  created_by_user?: { first_name: string; last_name: string };
  cancelled_by_user?: { first_name: string; last_name: string };
  share_sales?: ShareSale[];
}

interface ShareSale {
  id: string;
  shares_sold: number;
  average_sell_price?: number;
  total_value?: number;
  execution_date: string;
  trade_notes?: string;
}

interface HistoryEntry {
  id: string;
  old_status?: string;
  new_status: string;
  action_type: string;
  actor_type: string;
  actor_name?: string;
  metadata?: Record<string, unknown>;
  notes?: string;
  occurred_at: string;
}

const NoticeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isNovus } = useAuth();

  const [notice, setNotice] = useState<Notice | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [activateOpen, setActivateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [completeData, setCompleteData] = useState({ shares_sold: '', average_sell_price: '' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Check for success message from navigation
    if (location.state?.success) {
      setSuccess(location.state.success);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
    loadNotice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.state]);

  const loadNotice = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [noticeData, historyData] = await Promise.all([
        noticeAPI.getById(id),
        noticeAPI.getHistory(id)
      ]);
      setNotice(noticeData);
      setHistory(historyData);
    } catch (err: any) {
      setError('Failed to load notice');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await noticeAPI.activate(id);
      setSuccess('Notice activated successfully!');
      setActivateOpen(false);
      loadNotice();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate notice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await noticeAPI.cancel(id, cancelReason);
      setSuccess('Notice cancelled successfully!');
      setCancelOpen(false);
      setCancelReason('');
      loadNotice();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel notice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await noticeAPI.complete(id, {
        shares_sold: parseInt(completeData.shares_sold),
        average_sell_price: completeData.average_sell_price 
          ? parseFloat(completeData.average_sell_price) 
          : undefined
      });
      setSuccess('Notice completed successfully!');
      setCompleteOpen(false);
      setCompleteData({ shares_sold: '', average_sell_price: '' });
      loadNotice();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete notice');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string): 'warning' | 'success' | 'info' | 'error' | 'grey' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'expired': return 'error';
      case 'cancelled': return 'grey';
      default: return 'grey';
    }
  };
  
  const getChipStatusColor = (status: string): 'warning' | 'success' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'created': return <Schedule color="primary" />;
      case 'activated': return <PlayArrow color="success" />;
      case 'completed': return <CheckCircle color="info" />;
      case 'expired': return <Warning color="error" />;
      case 'cancelled': return <Cancel color="disabled" />;
      default: return <Schedule />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!notice) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Notice not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const totalSharesSold = notice.share_sales?.reduce((sum, sale) => sum + sale.shares_sold, 0) || 0;
  const totalValueRaised = notice.share_sales?.reduce((sum, sale) => sum + (sale.total_value || 0), 0) || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {notice.notice_number}
            </Typography>
            <Chip 
              label={notice.status.toUpperCase()} 
              color={getChipStatusColor(notice.status)}
              size="medium"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isAdmin && notice.status === 'pending' && (
              <>
                <Button 
                  variant="outlined" 
                  startIcon={<Edit />}
                  onClick={() => navigate(`/notices/${notice.id}/edit`)}
                >
                  Edit
                </Button>
                <Button variant="contained" color="success" onClick={() => setActivateOpen(true)}>
                  Activate
                </Button>
                <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
                  Cancel
                </Button>
              </>
            )}
            {isAdmin && notice.status === 'active' && (
              <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
                Cancel Notice
              </Button>
            )}
            {isNovus && notice.status === 'active' && (
              <Button variant="contained" onClick={() => setCompleteOpen(true)}>
                Complete Notice
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Notice Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notice Details</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Min Sell Price</Typography>
                  <Typography variant="h6">{formatCurrency(notice.min_sell_price)}</Typography>
                </Grid>
                {notice.min_price_allowed && (
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">Min Price Allowed</Typography>
                    <Typography variant="h6">{formatCurrency(notice.min_price_allowed)}</Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Shares Authorized</Typography>
                  <Typography variant="h6">{notice.share_quantity_total.toLocaleString()}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Active Period</Typography>
                  <Typography variant="body1">
                    {format(new Date(notice.start_date), 'MMM d, yyyy')} - {format(new Date(notice.end_date), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
              </Grid>

              {notice.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{notice.notes}</Typography>
                </Box>
              )}

              {notice.cancellation_reason && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Cancellation Reason:</Typography>
                  <Typography variant="body1">{notice.cancellation_reason}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Share Sales */}
          {notice.share_sales && notice.share_sales.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Share Sales</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Execution Date</TableCell>
                        <TableCell align="right">Shares Sold</TableCell>
                        <TableCell align="right">Avg Price</TableCell>
                        <TableCell align="right">Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notice.share_sales.map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell>{format(new Date(sale.execution_date), 'MMM d, yyyy')}</TableCell>
                          <TableCell align="right">{sale.shares_sold.toLocaleString()}</TableCell>
                          <TableCell align="right">{sale.average_sell_price ? formatCurrency(sale.average_sell_price) : '-'}</TableCell>
                          <TableCell align="right">{sale.total_value ? formatCurrency(sale.total_value) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* History Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Activity History</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Timeline position="right" sx={{ p: 0 }}>
                {history.map((entry, index) => (
                  <TimelineItem key={entry.id}>
                    <TimelineOppositeContent sx={{ flex: 0.3, minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(entry.occurred_at), 'MMM d, yyyy')}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(entry.occurred_at), 'HH:mm')}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={getStatusColor(entry.new_status)}>
                        {getActionIcon(entry.action_type)}
                      </TimelineDot>
                      {index < history.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="medium">
                        {entry.action_type.charAt(0).toUpperCase() + entry.action_type.slice(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.actor_type === 'user' ? entry.actor_name : 'System'}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Summary</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Shares Sold</Typography>
              <Typography variant="h5">
                {totalSharesSold.toLocaleString()} / {notice.share_quantity_total.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((totalSharesSold / notice.share_quantity_total) * 100).toFixed(1)}% utilized
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Capital Raised</Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(totalValueRaised)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">Shares Remaining</Typography>
              <Typography variant="h5">
                {(notice.share_quantity_total - totalSharesSold).toLocaleString()}
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Key Dates</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{format(new Date(notice.created_at), 'MMM d, yyyy HH:mm')}</Typography>
              </Box>
              {notice.activated_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Activated</Typography>
                  <Typography variant="body2">{format(new Date(notice.activated_at), 'MMM d, yyyy HH:mm')}</Typography>
                </Box>
              )}
              {notice.completed_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2">{format(new Date(notice.completed_at), 'MMM d, yyyy HH:mm')}</Typography>
                </Box>
              )}
              {notice.expired_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Expired</Typography>
                  <Typography variant="body2">{format(new Date(notice.expired_at), 'MMM d, yyyy HH:mm')}</Typography>
                </Box>
              )}
              {notice.cancelled_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Cancelled</Typography>
                  <Typography variant="body2">{format(new Date(notice.cancelled_at), 'MMM d, yyyy HH:mm')}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Activate Dialog */}
      <Dialog open={activateOpen} onClose={() => setActivateOpen(false)}>
        <DialogTitle>Activate Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate notice <strong>{notice.notice_number}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This will authorize Novus Capital to sell up to <strong>{notice.share_quantity_total.toLocaleString()}</strong> shares 
            at a minimum price of <strong>{formatCurrency(notice.min_sell_price)}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleActivate} variant="contained" color="success" disabled={actionLoading}>
            {actionLoading ? 'Activating...' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancel Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel notice <strong>{notice.notice_number}</strong>?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason (Optional)"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={actionLoading}>Keep Notice</Button>
          <Button onClick={handleCancel} variant="contained" color="error" disabled={actionLoading}>
            {actionLoading ? 'Cancelling...' : 'Cancel Notice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)}>
        <DialogTitle>Complete Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the share sale details to complete notice <strong>{notice.notice_number}</strong>.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Shares Sold"
            type="number"
            fullWidth
            required
            value={completeData.shares_sold}
            onChange={(e) => setCompleteData(prev => ({ ...prev, shares_sold: e.target.value }))}
            inputProps={{ max: notice.share_quantity_total }}
            helperText={`Maximum: ${notice.share_quantity_total.toLocaleString()}`}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Average Sell Price (Optional)"
            type="number"
            fullWidth
            value={completeData.average_sell_price}
            onChange={(e) => setCompleteData(prev => ({ ...prev, average_sell_price: e.target.value }))}
            inputProps={{ min: notice.min_sell_price, step: '0.0001' }}
            helperText={`Minimum: ${formatCurrency(notice.min_sell_price)}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button 
            onClick={handleComplete} 
            variant="contained" 
            disabled={actionLoading || !completeData.shares_sold}
          >
            {actionLoading ? 'Completing...' : 'Complete Notice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NoticeDetail;

